import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ShieldCheck, Loader2, Plus, RefreshCw, Ban, RotateCcw, Copy, Package, KeyRound, Users, ScrollText, LogOut,
} from "lucide-react";
import { api } from "@/lib/accessApi";

const TABS = [
  { id: "products", label: "Products", icon: Package },
  { id: "codes", label: "Access Codes", icon: KeyRound },
  { id: "entitlements", label: "Entitlements", icon: Users },
  { id: "audit", label: "Audit Logs", icon: ScrollText },
  { id: "admins", label: "Admins", icon: ShieldCheck },
];

export default function Admin() {
  const [admin, setAdmin] = useState(null); // null=checking | false=guest | object
  const [form, setForm] = useState({ email: "", password: "" });
  const [tab, setTab] = useState("products");

  useEffect(() => {
    (async () => {
      try { setAdmin(await api("/admin/auth/me")); }
      catch { setAdmin(false); }
    })();
  }, []);

  const login = async (e) => {
    e.preventDefault();
    try { setAdmin(await api("/admin/auth/login", { method: "POST", body: form })); toast.success("Welcome back"); }
    catch (err) { toast.error(err.detail || "Invalid email or password"); }
  };
  const logout = async () => { try { await api("/admin/auth/logout", { method: "POST" }); } catch { /* ignore */ } setAdmin(false); };

  if (admin === null) return <div className="flex min-h-screen items-center justify-center bg-slate-950 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  if (!admin) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950 px-4">
        <form onSubmit={login} className="w-full max-w-sm rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur">
          <div className="mb-5 flex flex-col items-center text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 to-sky-500"><ShieldCheck className="h-6 w-6 text-white" /></span>
            <h1 className="mt-3 text-lg font-bold text-white">Admin Login</h1>
            <p className="text-sm text-slate-400">Sign in with your admin email and password.</p>
          </div>
          <label className="mb-1 block text-xs text-slate-400">Email</label>
          <input
            type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="admin-email" placeholder="admin@example.com" autoComplete="username"
            className="mb-3 w-full rounded-xl border border-white/10 bg-slate-900/60 px-4 py-3 text-sm text-white outline-none focus:border-indigo-400"
          />
          <label className="mb-1 block text-xs text-slate-400">Password</label>
          <input
            type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
            data-testid="admin-password" placeholder="••••••••" autoComplete="current-password"
            className="w-full rounded-xl border border-white/10 bg-slate-900/60 px-4 py-3 text-sm text-white outline-none focus:border-indigo-400"
          />
          <button type="submit" data-testid="admin-login" className="mt-4 w-full rounded-xl bg-gradient-to-r from-indigo-500 to-sky-500 py-3 text-sm font-semibold text-white hover:opacity-90">Sign in</button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      <header className="border-b border-white/10 bg-slate-900/50 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-500 to-sky-500"><ShieldCheck className="h-4 w-4 text-white" /></span>
            <div>
              <h1 className="font-bold leading-tight text-white">Access Admin</h1>
              <p className="text-xs text-slate-400" data-testid="admin-current-email">{admin.email}</p>
            </div>
          </div>
          <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-1.5 rounded-lg border border-white/10 px-3 py-1.5 text-sm text-slate-300 hover:bg-white/5"><LogOut className="h-4 w-4" /> Log out</button>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-4 py-6">
        <div className="mb-6 flex flex-wrap gap-2">
          {TABS.map((t) => (
            <button
              key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
              className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition-colors ${tab === t.id ? "bg-indigo-500 text-white" : "border border-white/10 text-slate-300 hover:bg-white/5"}`}
            >
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "products" && <Products />}
        {tab === "codes" && <Codes />}
        {tab === "entitlements" && <Entitlements />}
        {tab === "audit" && <Audit />}
        {tab === "admins" && <AdminsPanel />}
      </div>
    </div>
  );
}

const inputCls = "w-full rounded-lg border border-white/10 bg-slate-900/60 px-3 py-2 text-sm text-white outline-none focus:border-indigo-400";
const Card = ({ children, className = "" }) => <div className={`rounded-2xl border border-white/10 bg-white/5 p-5 ${className}`}>{children}</div>;
const Btn = ({ children, className = "", ...p }) => <button {...p} className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium disabled:opacity-50 ${className}`}>{children}</button>;

function Products({ adminKey }) {
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({ name: "", description: "", content_md: "", attachments: "", lynk_ref: "", lynk_url: "" });
  const [editId, setEditId] = useState(null);

  const load = useCallback(async () => {
    const { products } = await api("/admin/products", { adminKey });
    setProducts(products);
  }, [adminKey]);
  useEffect(() => { load(); }, [load]);

  const parseAtt = (s) => s.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
    const [label, url] = l.split("|").map((x) => x.trim());
    return { label: url ? label : l, url: url || label };
  });

  const save = async (e) => {
    e.preventDefault();
    const body = { name: form.name, description: form.description, content_md: form.content_md, attachments: parseAtt(form.attachments), status: "ACTIVE", lynk_ref: form.lynk_ref, lynk_url: form.lynk_url };
    try {
      if (editId) await api(`/admin/products/${editId}`, { method: "PUT", body });
      else await api("/admin/products", { method: "POST", body });
      toast.success(editId ? "Product updated" : "Product created");
      setForm({ name: "", description: "", content_md: "", attachments: "", lynk_ref: "", lynk_url: "" });
      setEditId(null);
      load();
    } catch (err) { toast.error(err.detail || "Failed"); }
  };

  const edit = (p) => {
    setEditId(p.id);
    setForm({ name: p.name, description: p.description, content_md: p.content_md, attachments: (p.attachments || []).map((a) => `${a.label} | ${a.url}`).join("\n"), lynk_ref: p.lynk_ref || "", lynk_url: p.lynk_url || "" });
  };

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Card>
        <h2 className="mb-3 font-semibold text-white">{editId ? "Edit product" : "Create product"}</h2>
        <form onSubmit={save} className="space-y-3">
          <input className={inputCls} placeholder="Name" required data-testid="product-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <input className={inputCls} placeholder="Description" data-testid="product-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <textarea className={`${inputCls} min-h-28 font-mono`} placeholder="Content (Markdown)" data-testid="product-content" value={form.content_md} onChange={(e) => setForm({ ...form, content_md: e.target.value })} />
          <textarea className={`${inputCls} min-h-20`} placeholder={"Attachments, one per line: Label | https://url"} value={form.attachments} onChange={(e) => setForm({ ...form, attachments: e.target.value })} />
          <input className={inputCls} placeholder="Lynk product ref (id/slug/name untuk mencocokkan webhook)" data-testid="product-lynkref" value={form.lynk_ref} onChange={(e) => setForm({ ...form, lynk_ref: e.target.value })} />
          <input className={inputCls} placeholder="Lynk checkout URL (tombol beli untuk yang belum bayar)" data-testid="product-lynkurl" value={form.lynk_url} onChange={(e) => setForm({ ...form, lynk_url: e.target.value })} />
          <div className="flex gap-2">
            <Btn type="submit" data-testid="product-save" className="bg-indigo-500 text-white hover:bg-indigo-600"><Plus className="h-4 w-4" /> {editId ? "Update" : "Create"}</Btn>
            {editId && <Btn type="button" onClick={() => { setEditId(null); setForm({ name: "", description: "", content_md: "", attachments: "", lynk_ref: "", lynk_url: "" }); }} className="border border-white/10 text-slate-300">Cancel</Btn>}
          </div>
        </form>
      </Card>
      <Card>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-semibold text-white">Products ({products.length})</h2>
          <Btn onClick={load} className="border border-white/10 text-slate-300"><RefreshCw className="h-4 w-4" /></Btn>
        </div>
        <div className="space-y-2" data-testid="product-list">
          {products.map((p) => (
            <div key={p.id} className="flex items-center justify-between rounded-lg border border-white/10 bg-slate-900/40 px-3 py-2">
              <div><p className="text-sm font-medium text-white">{p.name}</p><p className="text-xs text-slate-400">{p.status} · {p.attachments?.length || 0} files</p></div>
              <Btn onClick={() => edit(p)} className="border border-white/10 text-slate-300">Edit</Btn>
            </div>
          ))}
          {products.length === 0 && <p className="text-sm text-slate-500">No products yet.</p>}
        </div>
      </Card>
    </div>
  );
}

function Codes({ adminKey }) {
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState("");
  const [count, setCount] = useState(5);
  const [codes, setCodes] = useState([]);
  const [generated, setGenerated] = useState([]);

  const loadProducts = useCallback(async () => {
    const { products } = await api("/admin/products", { adminKey });
    setProducts(products);
    if (products[0] && !productId) setProductId(products[0].id);
  }, [adminKey, productId]);
  const loadCodes = useCallback(async () => {
    if (!productId) return;
    const { codes } = await api(`/admin/codes?product_id=${productId}`, { adminKey });
    setCodes(codes);
  }, [adminKey, productId]);

  useEffect(() => { loadProducts(); }, [loadProducts]);
  useEffect(() => { loadCodes(); }, [loadCodes]);

  const generate = async () => {
    try {
      const { codes } = await api("/admin/codes", { method: "POST", adminKey, body: { product_id: productId, count: Number(count) } });
      setGenerated(codes.map((c) => c.code));
      toast.success(`${codes.length} codes generated`);
      loadCodes();
    } catch (err) { toast.error(err.detail || "Failed"); }
  };

  const action = async (id, kind) => {
    try {
      await api(`/admin/codes/${id}/${kind}`, { method: "POST", adminKey });
      toast.success(`Code ${kind}`);
      loadCodes();
    } catch (err) { toast.error(err.detail || "Failed"); }
  };

  const copyAll = () => { navigator.clipboard?.writeText(generated.join("\n")); toast.success("Copied"); };

  const statusColor = { UNUSED: "text-sky-300", ACTIVE: "text-emerald-300", REVOKED: "text-red-300", EXPIRED: "text-amber-300" };

  return (
    <div className="space-y-6">
      <Card>
        <h2 className="mb-3 font-semibold text-white">Generate codes</h2>
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-48 flex-1">
            <label className="mb-1 block text-xs text-slate-400">Product</label>
            <select className={inputCls} data-testid="codes-product" value={productId} onChange={(e) => setProductId(e.target.value)}>
              {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div className="w-28">
            <label className="mb-1 block text-xs text-slate-400">Count</label>
            <input type="number" min="1" max="500" className={inputCls} data-testid="codes-count" value={count} onChange={(e) => setCount(e.target.value)} />
          </div>
          <Btn onClick={generate} disabled={!productId} data-testid="codes-generate" className="bg-indigo-500 text-white hover:bg-indigo-600"><Plus className="h-4 w-4" /> Generate</Btn>
        </div>
        {generated.length > 0 && (
          <div className="mt-4 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3" data-testid="generated-codes">
            <div className="mb-2 flex items-center justify-between">
              <p className="text-xs font-medium text-emerald-300">Copy now — shown only once ({generated.length})</p>
              <Btn onClick={copyAll} className="border border-emerald-500/30 text-emerald-200"><Copy className="h-3.5 w-3.5" /> Copy all</Btn>
            </div>
            <div className="grid grid-cols-2 gap-1 font-mono text-xs text-emerald-200 sm:grid-cols-3">
              {generated.map((c) => <span key={c}>{c}</span>)}
            </div>
          </div>
        )}
      </Card>

      <Card>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-semibold text-white">Codes ({codes.length})</h2>
          <Btn onClick={loadCodes} className="border border-white/10 text-slate-300"><RefreshCw className="h-4 w-4" /></Btn>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm" data-testid="codes-table">
            <thead className="text-xs text-slate-400">
              <tr><th className="p-2">Code</th><th className="p-2">Status</th><th className="p-2">Bound email</th><th className="p-2">Actions</th></tr>
            </thead>
            <tbody className="text-slate-200">
              {codes.map((c) => (
                <tr key={c.id} className="border-t border-white/5" data-testid={`code-row-${c.id}`}>
                  <td className="p-2 font-mono">{c.masked}</td>
                  <td className={`p-2 font-semibold ${statusColor[c.status] || ""}`}>{c.status}</td>
                  <td className="p-2 text-slate-300">{c.bound_email || "—"}</td>
                  <td className="p-2">
                    <div className="flex gap-1.5">
                      <Btn onClick={() => action(c.id, "revoke")} data-testid={`revoke-${c.id}`} className="border border-red-500/30 text-red-300 hover:bg-red-500/10"><Ban className="h-3.5 w-3.5" /> Revoke</Btn>
                      <Btn onClick={() => action(c.id, "reset")} data-testid={`reset-${c.id}`} className="border border-white/10 text-slate-300 hover:bg-white/5"><RotateCcw className="h-3.5 w-3.5" /> Reset</Btn>
                    </div>
                  </td>
                </tr>
              ))}
              {codes.length === 0 && <tr><td colSpan={4} className="p-3 text-slate-500">No codes for this product.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function Entitlements({ adminKey }) {
  const [rows, setRows] = useState([]);
  const load = useCallback(async () => { const { entitlements } = await api("/admin/entitlements", { adminKey }); setRows(entitlements); }, [adminKey]);
  useEffect(() => { load(); }, [load]);
  const revoke = async (id) => { try { await api(`/admin/entitlements/${id}/revoke`, { method: "POST", adminKey }); toast.success("Entitlement revoked"); load(); } catch (e) { toast.error(e.detail || "Failed"); } };

  return (
    <Card>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="font-semibold text-white">Entitlements ({rows.length})</h2>
        <Btn onClick={load} className="border border-white/10 text-slate-300"><RefreshCw className="h-4 w-4" /></Btn>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm" data-testid="entitlements-table">
          <thead className="text-xs text-slate-400"><tr><th className="p-2">Email</th><th className="p-2">Product</th><th className="p-2">Status</th><th className="p-2">Actions</th></tr></thead>
          <tbody className="text-slate-200">
            {rows.map((e) => (
              <tr key={e.id} className="border-t border-white/5">
                <td className="p-2">{e.email}</td>
                <td className="p-2 font-mono text-xs">{e.product_id.slice(0, 8)}</td>
                <td className={`p-2 font-semibold ${e.status === "ACTIVE" ? "text-emerald-300" : "text-red-300"}`}>{e.status}</td>
                <td className="p-2">{e.status === "ACTIVE" && <Btn onClick={() => revoke(e.id)} data-testid={`revoke-ent-${e.id}`} className="border border-red-500/30 text-red-300 hover:bg-red-500/10"><Ban className="h-3.5 w-3.5" /> Revoke</Btn>}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={4} className="p-3 text-slate-500">No entitlements yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function Audit({ adminKey }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const { logs } = await api("/admin/audit", { adminKey }); setLogs(logs); } finally { setLoading(false); }
  }, [adminKey]);
  useEffect(() => { load(); }, [load]);

  return (
    <Card>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="font-semibold text-white">Audit Logs ({logs.length})</h2>
        <Btn onClick={load} className="border border-white/10 text-slate-300"><RefreshCw className="h-4 w-4" /></Btn>
      </div>
      <div className="space-y-1.5" data-testid="audit-list">
        {loading && <p className="text-sm text-slate-500">Loading…</p>}
        {!loading && logs.map((l) => (
          <div key={l.id} className="flex items-center justify-between rounded-lg border border-white/5 bg-slate-900/40 px-3 py-2 text-sm">
            <span className="font-mono text-xs text-indigo-300">{l.action}</span>
            <span className="text-slate-300">{l.email || "—"}</span>
            <span className="text-xs text-slate-500">{l.created_at?.slice(0, 19).replace("T", " ")}</span>
          </div>
        ))}
        {!loading && logs.length === 0 && <p className="text-sm text-slate-500">No activity yet.</p>}
      </div>
    </Card>
  );
}

function AdminsPanel() {
  const [form, setForm] = useState({ email: "", password: "" });
  const register = async (e) => {
    e.preventDefault();
    try {
      await api("/admin/auth/register", { method: "POST", body: form });
      toast.success("Admin account created");
      setForm({ email: "", password: "" });
    } catch (err) { toast.error(err.detail || "Failed to create admin"); }
  };
  return (
    <Card className="max-w-md">
      <h2 className="mb-1 font-semibold text-white">Create admin account</h2>
      <p className="mb-3 text-sm text-slate-400">New admins can sign in with their email &amp; password.</p>
      <form onSubmit={register} className="space-y-3">
        <input type="email" required className={inputCls} placeholder="Email" data-testid="new-admin-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <input type="password" required minLength={8} className={inputCls} placeholder="Password (min 8 chars)" data-testid="new-admin-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
        <Btn type="submit" data-testid="new-admin-submit" className="bg-indigo-500 text-white hover:bg-indigo-600"><Plus className="h-4 w-4" /> Create admin</Btn>
      </form>
    </Card>
  );
}
